import { Pencil, X } from "lucide-react";
import { cloneElement, useEffect, useState, type FormEvent, type ReactElement, type ReactNode } from "react";
import { apiRequest, describeApiError } from "../lib/api";
import { todayIso } from "../lib/format";
import { uploadPhoto, useDreamPhotos } from "../lib/queries";
import type { ApiEnvelope, Wallet } from "../types";
import { Modal } from "./Modal";
import { PhotoPicker } from "./PhotoPicker";
import { ButtonLabel } from "./ButtonLabel";

/** Values a caller can prefill instead of the wallet's current goal (the trip page's "Crear meta"). */
export interface GoalDialogPrefill { title: string; amount: string; date: string }

/**
 * "Editar mi sueño": title, target, date and photo of an existing Wallet Libre. Uncontrolled it
 * renders its own trigger; with `open`/`onOpenChange` the caller opens it (the trip page's "Crear
 * meta" sends the customer here, prefilled with the trip, so the photo can be changed too).
 */
export function GoalDialog({ wallet, trigger, onSaved, open: controlledOpen, onOpenChange, prefill, hint, footnote }: {
  wallet: Wallet;
  trigger?: ReactElement<{ onClick?: () => void }>;
  onSaved(wallet: Wallet): void;
  open?: boolean;
  onOpenChange?(open: boolean): void;
  prefill?: GoalDialogPrefill;
  /** Shown under the amount (the trip's suggested target). */
  hint?: ReactNode;
  /** Shown under the save button. */
  footnote?: ReactNode;
}) {
  const controlled = controlledOpen !== undefined;
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlled ? controlledOpen : ownOpen;
  const setOpen = (next: boolean) => { if (controlled) onOpenChange?.(next); else setOwnOpen(next); };
  const [title, setTitle] = useState(wallet.goal?.title ?? "");
  const [amount, setAmount] = useState(wallet.goal?.amount ? String(wallet.goal.amount) : "");
  const [date, setDate] = useState(wallet.goal?.date ?? "");
  const [photoId, setPhotoId] = useState<string | null>(wallet.goal?.photoId ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const photos = useDreamPhotos();

  const reset = () => {
    setTitle(prefill?.title ?? wallet.goal?.title ?? "");
    setAmount(prefill?.amount ?? (wallet.goal?.amount ? String(wallet.goal.amount) : ""));
    setDate(prefill?.date ?? wallet.goal?.date ?? "");
    setPhotoId(wallet.goal?.photoId ?? null); setError(null);
  };
  const openDialog = () => { reset(); setOpen(true); };

  // A controlled dialog is opened by its caller, so it refreshes its fields when that happens.
  useEffect(() => {
    if (controlled && controlledOpen) reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled, controlledOpen]);

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(null);
    const numeric = Number(amount);
    if (amount && (!Number.isFinite(numeric) || numeric <= 0)) { setError(["Ingresa un monto válido."]); return; }
    setSubmitting(true);
    try {
      const payload = await apiRequest<ApiEnvelope<{ wallet: Wallet }>>(`/api/v1/wallets/${wallet.id}/goal`, {
        method: "PATCH",
        body: JSON.stringify({
          goalTitle: title.trim() || null,
          goalAmount: amount ? numeric : null,
          goalDate: date || null,
          goalPhotoId: photoId
        })
      });
      onSaved(payload.data.wallet);
      setOpen(false);
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos guardar tu sueño."));
    } finally { setSubmitting(false); }
  };

  return (
    <>
      {controlled ? null : trigger
        ? cloneElement(trigger, { onClick: openDialog })
        : <button type="button" className="glass-button" onClick={openDialog}><Pencil size={14} aria-hidden="true" /> Editar mi sueño</button>}
      <Modal open={open} onOpenChange={setOpen} label="Editar mi sueño">
        <div className="dialog-content wide">
          <span className="page-eyebrow">Tu sueño</span>
          <h2>Edita tu sueño</h2>
          <p>Sin penalización: puedes mover la fecha o subir el monto en cualquier momento.</p>
          <form className="dialog-form" onSubmit={submit}>
            <div className="field-block">
              <label htmlFor="goal-title">Título del viaje</label>
              <input id="goal-title" className="standalone-input" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} placeholder="Luna de miel en Maldivas" />
            </div>
            <div className="field-grid">
              <div className="field-block">
                <label htmlFor="goal-amount">Monto a ahorrar (MXN)</label>
                <input id="goal-amount" className="standalone-input" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="4800" />
                {hint ? <span className="field-hint">{hint}</span> : null}
              </div>
              <div className="field-block">
                <label htmlFor="goal-date">Fecha objetivo</label>
                <input id="goal-date" className="standalone-input" type="date" min={todayIso(1)} value={date} onChange={(event) => setDate(event.target.value)} />
              </div>
            </div>
            <div className="field-block">
              <span className="field-label">Foto de tu sueño</span>
              <PhotoPicker photos={photos.data ?? []} value={photoId} onChange={setPhotoId} allowNone onUpload={(file) => uploadPhoto(file, { title: title || "Mi sueño" })} />
            </div>
            {error && <div className="form-error" role="alert">{error.join(" ")}</div>}
            <button className="primary-button full" type="submit" disabled={submitting}><ButtonLabel busy={submitting} busyLabel="Guardando" label="Guardar cambios" /></button>
            {footnote ? <p className="resv-fine">{footnote}</p> : null}
          </form>
          <button type="button" className="dialog-close" onClick={() => setOpen(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
        </div>
      </Modal>
    </>
  );
}
